import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import undoable from 'redux-undo';
import { isPresent } from 'utils/isPresent';
import inMemoryStore from '../InMemoryStore.ts';
import { handleEntities } from './DataFetcherEntitiesHelper';
import reducerRegistry from './ReducerRegistry';
import { DATA_FETCHED, UNDO, CLEAR_DATA } from './DataConstants';
import { isBlank } from 'utils/isBlank';

export function FetchData(fetchingHash, renderAfterLoading = false) {
  return (WrappedComponent) => {
    class DataFetcher extends PureComponent {
      constructor(props) {
        super(props);
        this.state = {
          dataFetcherLoading: false,
          isMounted: false,
        };
      }

      componentDidMount() {
        if (isBlank(this.props.data)) {
          this.loading(true);
          this.props.fetchData(fetchingHash, this.props).then(() => {
            this.loading(false);
          });
        }
        this.setState({ isMounted: true });
      }

      loading(status) {
        this.setState({
          dataFetcherLoading: status,
        });
      }

      renderBeforeLoading() {
        return !renderAfterLoading;
      }

      render() {
        const { ...rest } = this.props;
        if (this.dataIsPresent() || this.renderBeforeLoading() || this.dataIsFetched()) {
          return <WrappedComponent loading={this.state.dataFetcherLoading} {...rest} />;
        } else {
          return <div></div>;
        }
      }

      dataIsPresent() {
        return !isBlank(this.props.data);
      }

      dataIsFetched() {
        return this.state.isMounted && !this.state.dataFetcherLoading;
      }
    }

    return connect(
      (state, props) => ({
        data: fetchingHash.selector(state, props),
      }),
      { fetchData }
    )(DataFetcher);
  };
}

export function fetchData(fetchingHash, props) {
  let params = [];

  return (dispatch, getState) => {
    if (fetchingHash.paramsFromProps) {
      params = fetchingHash.paramsFromProps(props, getState());
    }

    if (pendingRequest(fetchingHash)) {
      return createCachedPromise(fetchingHash);
    }
    addToPendingRequests(fetchingHash);
    const promise = fetchingHash
      .fetchingApiFunc(...params)
      .then((response) => {
        let { data } = response;
        data = handleLegacyDisintegratedObjectsSchema(fetchingHash, data, dispatch, getState);

        if (isPresent(fetchingHash.schema)) {
          handleEntities(fetchingHash.schema, data, dispatch, getState(), fetchingHash.schemaKey);
        } else if (isPresent(fetchingHash.propertyName)) {
          dispatch(receivedServerData({ [fetchingHash.propertyName]: data }, fetchingHash.dataNamespace));
        } else {
          dispatch(receivedServerData(data, fetchingHash.dataNamespace));
        }
        resolvePromisesAndRemoveFromPendingRequests(fetchingHash, response);
        return response;
      })
      .catch((error) => {
        rejectPromisesAndRemoveFromPendingRequests(fetchingHash, error);
        console.error(error);
        throw error;
      });
    return promise;
  };
}

let handleLegacyDisintegratedObjectsSchema = function (fetchingHash, data, dispatch, getState) {
  const { disintegrateObjectNamespace } = fetchingHash;
  if (isPresent(disintegrateObjectNamespace)) {
    const listDataWithObjects = data.data;

    const listDataWithIds = listDataWithObjects.map((item) => item.id);
    const objectMap = listDataWithObjects.reduce((map, obj) => {
      map[obj.id] = obj;
      return map;
    }, {});

    data.data = listDataWithIds;
    updateCache('objects', objectMap, disintegrateObjectNamespace, dispatch, getState());
  }
  return data;
};

function createCachedPromise(fetchingHash) {
  return new Promise((resolve, reject) => {
    const { resolveList, rejectList } = inMemoryStore.read(fetchingHash.uniqueCacheKey);
    resolveList.push(resolve);
    rejectList.push(reject);
  });
}

function pendingRequest(fetchingHash) {
  const { uniqueCacheKey } = fetchingHash;
  return isPresent(uniqueCacheKey) && isPresent(inMemoryStore.read(uniqueCacheKey));
}

function addToPendingRequests(fetchingHash) {
  const { uniqueCacheKey } = fetchingHash;
  if (isPresent(uniqueCacheKey)) {
    inMemoryStore.write(uniqueCacheKey, { resolveList: [], rejectList: [] });
  }
}

function resolvePromisesAndRemoveFromPendingRequests(fetchingHash, response) {
  const { uniqueCacheKey } = fetchingHash;
  if (isPresent(uniqueCacheKey)) {
    const { resolveList } = inMemoryStore.read(uniqueCacheKey);
    resolveList.forEach((resolve) => resolve(response));
    inMemoryStore.remove(uniqueCacheKey);
  }
}

function rejectPromisesAndRemoveFromPendingRequests(fetchingHash, error) {
  const { uniqueCacheKey } = fetchingHash;
  if (isPresent(uniqueCacheKey)) {
    const { rejectList } = inMemoryStore.read(uniqueCacheKey);
    rejectList.forEach((reject) => reject(error));
    inMemoryStore.remove(uniqueCacheKey);
  }
}

export function updateCache(key, updatedFields, namespace, dispatch, state) {
  const foundNamespace = state[namespace] || {};
  let foundItem = foundNamespace[key] || {};
  let updatedFoundItem = Object.assign(foundItem, updatedFields);
  let copiedItem = Object.assign({}, updatedFoundItem);

  updateStore(key, copiedItem, namespace, dispatch);

  return copiedItem;
}

export function receivedServerData(data, namespace) {
  if (isBlank(namespace)) {
    namespace = Object.keys(data)[0];
    data = data[namespace];
  }

  if (reducerRegistry.isReducerPresent(namespace) === false) {
    reducerRegistry.register(namespace, createReducer(data, namespace));
  }
  return {
    type: `${DATA_FETCHED}_${namespace}`,
    data: data,
  };
}

export function updateCacheWithReversibleChange(key, updatedFields, namespace, dispatch, state) {
  dispatch(clearUndo(namespace));

  let foundItem = state[namespace][key] || {};
  updateUndoStore(foundItem, namespace, dispatch);

  let updatedItem = Object.assign(foundItem, updatedFields);
  updateUndoStore(updatedItem, namespace, dispatch);

  updateStore(key, updatedItem, namespace, dispatch);

  return updatedItem;
}

export function clearServerData(namespace) {
  return { type: `${CLEAR_DATA}_${namespace}` };
}

export function undoLastChange(namespace, dispatch, getState) {
  let undoNameSpace = `${UNDO}_${namespace}`;
  dispatch({ type: `${UNDO}_${undoNameSpace}` });

  return getState()[undoNameSpace].present;
}

export function updateStore(key, data, namespace, dispatch) {
  let dataHash = {};
  dataHash[key] = data;
  dispatch(receivedServerData({...dataHash}, namespace));
}

function updateUndoStore(data, namespace, dispatch) {
  let undoNameSpace = `${UNDO}_${namespace}`;

  if (reducerRegistry.isReducerPresent(undoNameSpace) === false) {
    reducerRegistry.register(
      undoNameSpace,
      undoable(createUndoReducer(data, undoNameSpace), {
        limit: 1,
        undoType: `${UNDO}_${undoNameSpace}`,
      })
    );
  }
  dispatch({
    type: `${DATA_FETCHED}_${undoNameSpace}`,
    data: data,
  });
}

function clearUndo(namespace) {
  let undoNameSpace = `${UNDO}_${namespace}`;
  return { type: `${CLEAR_DATA}_${undoNameSpace}` };
}

const createReducer =
  (data, namespace) =>
  (state = data, action) => {
    switch (action.type) {
      case `${DATA_FETCHED}_${namespace}`:
        if (Array.isArray(action.data)) {
          return action.data;
        }
        return Object.assign({}, state, action.data);
      case `${CLEAR_DATA}_${namespace}`:
        return {};
      default:
        return state;
    }
  };

const createUndoReducer =
  (data, namespace) =>
  (state = data, action) => {
    switch (action.type) {
      case `${DATA_FETCHED}_${namespace}`:
        return {...action.data};
      case `${CLEAR_DATA}_${namespace}`:
        return {};
      default:
        return state;
    }
  };
